import { useEffect, useState } from "react";
import { api } from "./api";

const QKEY = "practitality_sync_queue";

export function cacheSet(key, data) {
  try { localStorage.setItem(`cache:${key}`, JSON.stringify(data)); } catch {}
}

export function cacheGet(key) {
  try {
    const v = localStorage.getItem(`cache:${key}`);
    return v ? JSON.parse(v) : null;
  } catch { return null; }
}

export async function cachedGet(path, key, params) {
  try {
    const { data } = await api.get(path, { params });
    cacheSet(key, data);
    return { data, fromCache: false };
  } catch (e) {
    if (!e.response) {
      const c = cacheGet(key);
      if (c) return { data: c, fromCache: true };
    }
    throw e;
  }
}

export function getQueue() {
  try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; }
}

function setQueue(q) {
  localStorage.setItem(QKEY, JSON.stringify(q));
  window.dispatchEvent(new Event("sync-queue-changed"));
}

export function enqueue(item) {
  try {
    setQueue([...getQueue(), { ...item, queued_at: new Date().toISOString() }]);
    return true;
  } catch {
    return false;
  }
}

export async function syncQueue() {
  const q = getQueue();
  if (!q.length) return 0;
  let synced = 0;
  const remaining = [];
  for (const item of q) {
    try {
      await api.post(item.path, item.body);
      synced++;
    } catch (e) {
      if (e.response && e.response.status < 500) synced++;
      else remaining.push(item);
    }
  }
  setQueue(remaining);
  return synced;
}

export function useOffline() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(getQueue().length);

  useEffect(() => {
    const update = () => setPending(getQueue().length);
    const onUp = async () => { setOnline(true); await syncQueue(); };
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    window.addEventListener("sync-queue-changed", update);
    return () => {
      window.removeEventListener("online", onUp);
      window.removeEventListener("offline", onDown);
      window.removeEventListener("sync-queue-changed", update);
    };
  }, []);

  return { online, pending, sync: syncQueue };
}
